
import { useState, useEffect } from "react";
import { toast } from "@/components/ui/use-toast";
import { getCities } from "@/services/cityService";
import { getOrganizations } from "@/services/organizationService";
import { supabase } from "@/integrations/supabase/client";
import { City, Organization } from "@/types/flight";

export function useCityData() {
  const [cities, setCities] = useState<City[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCities = async () => {
    try {
      const data = await getCities();
      setCities(data);
    } catch (error) {
      console.error('Error fetching cities:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch cities",
      });
    }
  };

  const fetchOrganizations = async () => {
    try {
      const data = await getOrganizations();
      setOrganizations(data);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCities(), fetchOrganizations()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this city?")) return;

    try {
      const { error } = await supabase
        .from('cities')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "City deleted",
        description: "The city has been deleted successfully.",
      });
      fetchCities();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to delete city",
      });
    }
  };

  return {
    cities,
    organizations,
    loading,
    fetchCities,
    handleDelete,
  };
}
